import ScrollToTop from "@/components/ScrollToTop";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ProtectedRoute } from "./components/admin/ProtectedRoute";
import { PublicLayout } from "./layouts/PublicLayout";

const Index = lazy(() => import("./pages/Index"));
const ResearchListing = lazy(() => import("./pages/ResearchListing"));
const ResearchDetail = lazy(() => import("./pages/ResearchDetail"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const BoardPage = lazy(() => import("./pages/BoardPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const ProjetosListingPage = lazy(() => import("./pages/ProjetosListingPage"));
const ProjetoDetailPage = lazy(() => import("./pages/ProjetoDetailPage"));
const TransparencyPage = lazy(() => import("./pages/TransparencyPage"));
const CTECNOsMainPage = lazy(() => import("./pages/CTECNOsMainPage"));
const AgendarVisitaPage = lazy(() => import("./pages/AgendarVisitaPage"));
const MediaPage = lazy(() => import("./pages/MediaPage"));
const PublicationsPage = lazy(() => import("./pages/PublicationsPage"));
const BoletinsPage = lazy(() => import("./pages/publications/BoletinsPage"));
const CircularesPage = lazy(() => import("./pages/publications/CircularesPage"));
const InstitucionalPage = lazy(() => import("./pages/InstitucionalPage"));
const NoticiasListingPage = lazy(() => import("./pages/NoticiasListingPage"));
const NoticiaDetailPage = lazy(() => import("./pages/NoticiaDetailPage"));
const CompliancePage = lazy(() => import("./pages/CompliancePage"));
const SolicitacaoApoio = lazy(() => import("./pages/SolicitacaoApoio"));
const OuvidoriaPage = lazy(() => import('./pages/OuvidoriaPage'));
const ComiteGestor = lazy(() => import("./pages/ComiteGestor"));
const EmailPreview = lazy(() => import("./pages/EmailPreview"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminResetPasswordPage = lazy(() => import("./pages/AdminResetPasswordPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const AdminBannersPage = lazy(() => import("./pages/admin/AdminBannersPage"));
const AdminBannerFormPage = lazy(() => import("./pages/admin/AdminBannerFormPage"));
const AdminCTECNOsPage = lazy(() => import("./pages/admin/AdminCTECNOsPage"));
const AdminCTECNOFormPage = lazy(() => import("./pages/admin/AdminCTECNOFormPage"));
const AdminCTECNOPhotosPage = lazy(() => import("./pages/admin/AdminCTECNOPhotosPage"));
const AdminCTECNOTeamPage = lazy(() => import("./pages/admin/AdminCTECNOTeamPage"));
const AdminProjetosPage = lazy(() => import("./pages/admin/AdminProjetosPage"));
const AdminProjetoFormPage = lazy(() => import("./pages/admin/AdminProjetoFormPage"));
const AdminAgendamentosPage = lazy(() => import("./pages/admin/AdminAgendamentosPage"));
const AdminUsuariosPage = lazy(() => import("./pages/admin/AdminUsuariosPage"));
const AdminAdministradoresPage = lazy(() => import("./pages/admin/AdminAdministradoresPage"));
const AdminPublicationsPage = lazy(() => import("./pages/admin/AdminPublicationsPage"));
const AdminPublicationFormPage = lazy(() => import("./pages/admin/AdminPublicationFormPage"));
const AdminPublicacoesEditoriaisPage = lazy(() => import("./pages/admin/AdminPublicacoesEditoriaisPage"));
const AdminMediaPage = lazy(() => import("./pages/admin/AdminMediaPage"));
const AdminPartnersPage = lazy(() => import("./pages/admin/AdminPartnersPage"));
const AdminPartnerFormPage = lazy(() => import("./pages/admin/AdminPartnerFormPage"));
const AdminTransparencyPage = lazy(() => import("./pages/admin/AdminTransparencyPage"));
const AdminIndicatorsPage = lazy(() => import("./pages/admin/AdminIndicatorsPage"));
const AdminPagesPage = lazy(() => import("./pages/admin/AdminPagesPage"));
const AdminPageFormPage = lazy(() => import("./pages/admin/AdminPageFormPage"));
const AdminNoticiasPage = lazy(() => import("./pages/admin/AdminNoticiasPage"));
const AdminNoticiaFormPage = lazy(() => import("./pages/admin/AdminNoticiaFormPage"));
const AdminCompliancePage = lazy(() => import("./pages/admin/AdminCompliancePage"));
const AdminComplianceFormPage = lazy(() => import("./pages/admin/AdminComplianceFormPage"));
const SolicitacoesApoio = lazy(() => import("./pages/admin/SolicitacoesApoio"));
const AdminOuvidoriaPage = lazy(() => import('./pages/admin/AdminOuvidoriaPage'));
const AdminMigracaoS3Page = lazy(() => import("./pages/admin/AdminMigracaoS3Page"));
const CMSPreviewPage = lazy(() => import("./pages/CMSPreviewPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const SentryFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="text-center space-y-4 max-w-md mx-auto p-8">
      <h1 className="text-2xl font-bold">Algo deu errado</h1>
      <p className="text-muted-foreground">
        Ocorreu um erro inesperado. Nossa equipe foi notificada automaticamente.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
      >
        Recarregar página
      </button>
    </div>
  </div>
);

const App = () => (
  <Sentry.ErrorBoundary fallback={<SentryFallback />} showDialog={false}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Index />} />
                <Route path="/pesquisa" element={<ResearchListing />} />
                <Route path="/pesquisa/:id" element={<ResearchDetail />} />
                <Route path="/quem-somos" element={<AboutPage />} />
                <Route path="/comite-gestor" element={<ComiteGestor />} />
                <Route path="/diretoria" element={<BoardPage />} />
                <Route path="/parceiros" element={<PartnersPage />} />
                <Route path="/transparencia" element={<TransparencyPage />} />
                <Route path="/ctecnos" element={<CTECNOsMainPage />} />
                <Route path="/projetos" element={<ProjetosListingPage />} />
                <Route path="/projetos/:id" element={<ProjetoDetailPage />} />
                <Route path="/contato/agende-visita" element={<AgendarVisitaPage />} />
                <Route path="/comunicacao/midias" element={<MediaPage />} />
                <Route path="/publicacoes" element={<PublicationsPage />} />
                <Route path="/publicacoes/boletim-tecnico" element={<BoletinsPage />} />
                <Route path="/publicacoes/circular-tecnica" element={<CircularesPage />} />
                <Route path="/institucional/:slug" element={<InstitucionalPage />} />
                <Route path="/noticias" element={<NoticiasListingPage />} />
                <Route path="/comunicacao/noticias" element={<NoticiasListingPage />} />
                <Route path="/comunicacao/noticias/:slug" element={<NoticiaDetailPage />} />
                <Route path="/compliance" element={<CompliancePage />} />
                <Route path="/compliance/:id" element={<CompliancePage />} />
                <Route path="/compliance/categoria/:cat" element={<CompliancePage />} />
                <Route path="/solicitacao-apoio" element={<SolicitacaoApoio />} />
                <Route path="/contato/ouvidoria" element={<OuvidoriaPage />} />
                <Route path="/email-preview" element={<EmailPreview />} />
              </Route>

              {/* Admin Login (no layout) */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
              <Route path="/admin" element={<AdminLoginPage />} />

              {/* Admin Routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="/admin/banners" element={<AdminBannersPage />} />
                <Route path="/admin/banners/novo" element={<AdminBannerFormPage />} />
                <Route path="/admin/banners/:id/editar" element={<AdminBannerFormPage />} />
                <Route path="/admin/ctecnos" element={<AdminCTECNOsPage />} />
                <Route path="/admin/ctecnos/novo" element={<AdminCTECNOFormPage />} />
                <Route path="/admin/ctecnos/:id/editar" element={<AdminCTECNOFormPage />} />
                <Route path="/admin/ctecnos/:id/fotos" element={<AdminCTECNOPhotosPage />} />
                <Route path="/admin/ctecnos/:id/equipe" element={<AdminCTECNOTeamPage />} />
                <Route path="/admin/projetos" element={<AdminProjetosPage />} />
                <Route path="/admin/projetos/novo" element={<AdminProjetoFormPage />} />
                <Route path="/admin/projetos/:id" element={<AdminProjetoFormPage />} />
                <Route path="/admin/agendamentos" element={<AdminAgendamentosPage />} />
                <Route path="/admin/usuarios" element={<AdminUsuariosPage />} />
                <Route
                  path="/admin/administradores"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminAdministradoresPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin/publicacoes" element={<AdminPublicationsPage />} />
                <Route path="/admin/publicacoes/nova" element={<AdminPublicationFormPage />} />
                <Route path="/admin/publicacoes/:id/editar" element={<AdminPublicationFormPage />} />
                <Route path="/admin/publicacoes-editoriais" element={<AdminPublicacoesEditoriaisPage />} />
                <Route path="/admin/midias" element={<AdminMediaPage />} />
                <Route path="/admin/parceiros" element={<AdminPartnersPage />} />
                <Route path="/admin/parceiros/novo" element={<AdminPartnerFormPage />} />
                <Route path="/admin/parceiros/:id/editar" element={<AdminPartnerFormPage />} />
                <Route path="/admin/transparencia" element={<AdminTransparencyPage />} />
                <Route path="/admin/indicadores" element={<AdminIndicatorsPage />} />
                <Route path="/admin/paginas" element={<AdminPagesPage />} />
                <Route path="/admin/paginas/nova" element={<AdminPageFormPage />} />
                <Route path="/admin/paginas/:id/editar" element={<AdminPageFormPage />} />
                <Route path="/admin/noticias" element={<AdminNoticiasPage />} />
                <Route path="/admin/noticias/nova" element={<AdminNoticiaFormPage />} />
                <Route path="/admin/noticias/:id/editar" element={<AdminNoticiaFormPage />} />
                <Route path="/admin/compliance" element={<AdminCompliancePage />} />
                <Route path="/admin/compliance/novo" element={<AdminComplianceFormPage />} />
                <Route path="/admin/compliance/:id/editar" element={<AdminComplianceFormPage />} />
                <Route path="/admin/compliance/:id/visualizacoes" element={<AdminCompliancePage />} />
                <Route path="/admin/solicitacoes-apoio" element={<SolicitacoesApoio />} />
                <Route path="/admin/ouvidoria" element={<AdminOuvidoriaPage />} />
                <Route path="/admin/migracao-s3" element={<AdminMigracaoS3Page />} />
                <Route path="/admin/cms-preview" element={<CMSPreviewPage />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
